import { EntitySelectors, EntityState } from '@reduxjs/toolkit';
import { SortingState } from '@tanstack/react-table';

import { JobsState } from '../jobSlice';
import { ScheduleListState } from './scheduleListSlice';
import { ToastState } from '../toasts';
import { JobOverview } from 'core/models/job';
import {
  filterBy,
  singleValueFilterFactory,
  storeFilterFactory,
} from './listLoader';

export interface Filters {
  queries: EntityState<FilterValue, string>;
  stores: EntityState<FilterValue, string>;
  authors: EntityState<FilterValue, string>;
  status: EntityState<FilterValue, string>;
  repetitionType: EntityState<FilterValue, string>;
}

export interface FilterToggledAction {
  type: string;
  value: string;
  shouldBeSelected: boolean;
  shouldForceReload?: boolean;
}

export interface SortingChangedAction {
  newSort: SortingState;
}

export interface PagingState {
  pageIndex: number;
  pageSize: number;
}

export interface PagingChangedAction {
  pageIndex: number;
}

export interface ScheduleStatusChange {
  scheduleId: number;
  newStatus: string;
}

export interface ScheduleDeletedAction {
  scheduleId: number;
}

export interface ActiveFilters {
  queries: string[];
  stores: string[];
  authors: string[];
  status: string[];
  repetitionType: string[];
}

export interface FilterValue {
  label: string;
  value: string;
  occurences?: number;
  baseOccurences?: number;
}

interface SchedulesRootState {
  jobs: JobsState;
  schedules: ScheduleListState;
  toasts: ToastState;
}

export interface FilterSelectors {
  queries: EntitySelectors<FilterValue, SchedulesRootState, string>;
  stores: EntitySelectors<FilterValue, SchedulesRootState, string>;
  authors: EntitySelectors<FilterValue, SchedulesRootState, string>;
  status: EntitySelectors<FilterValue, SchedulesRootState, string>;
  repetitionType: EntitySelectors<FilterValue, SchedulesRootState, string>;
}

export interface Filter {
  key: string;
  // getFilterFunc is used to filter the schedules by all OTHER filters. This is used when calculating filter values occurences. We need to know what schedules are left after filtering by other applied filters.
  getFilterFunc(
    state: ScheduleListState
  ): (schedules: JobOverview[]) => JobOverview[];
  getPickerFunc(): (schedule: JobOverview) => string | string[];
  getLabelPickerFunc(): (schedule: JobOverview) => string;
}

export const queryFilter: Filter = {
  key: 'queries',
  getPickerFunc() {
    return (schedule: JobOverview) => schedule.queryName;
  },
  getLabelPickerFunc() {
    return (schedule: JobOverview) => schedule.queryName;
  },
  getFilterFunc(state) {
    return (schedules: JobOverview[]) =>
      filterBy(
        filterBy(
          filterBy(
            filterBy(
              schedules,
              state.selectedFilters.authors,
              singleValueFilterFactory(
                state.selectedFilters.authors,
                j => j.authorName
              )
            ),
            state.selectedFilters.status,
            singleValueFilterFactory(
              state.selectedFilters.status,
              j => j.status
            )
          ),
          state.selectedFilters.repetitionType,
          singleValueFilterFactory(
            state.selectedFilters.repetitionType,
            j => j.repetition?.type?.toLowerCase() || ''
          )
        ),
        state.selectedFilters.stores,
        storeFilterFactory(state.selectedFilters.stores)
      );
  },
};

export const storesFilter: Filter = {
  key: 'stores',
  getPickerFunc() {
    return (schedule: JobOverview) =>
      schedule.stores.map(storeDto => storeDto.id);
  },
  getLabelPickerFunc() {
    return (_: JobOverview) => 'unsupported';
  },
  getFilterFunc(state) {
    return (schedules: JobOverview[]) =>
      filterBy(
        filterBy(
          filterBy(
            filterBy(
              schedules,
              state.selectedFilters.authors,
              singleValueFilterFactory(
                state.selectedFilters.authors,
                j => j.authorName
              )
            ),
            state.selectedFilters.status,
            singleValueFilterFactory(
              state.selectedFilters.status,
              j => j.status
            )
          ),
          state.selectedFilters.repetitionType,
          singleValueFilterFactory(
            state.selectedFilters.repetitionType,
            j => j.repetition?.type?.toLowerCase() || ''
          )
        ),
        state.selectedFilters.queries,
        singleValueFilterFactory(
          state.selectedFilters.queries,
          j => j.queryName
        )
      );
  },
};

export const authorsFilter: Filter = {
  key: 'authors',
  getPickerFunc() {
    return (schedule: JobOverview) => schedule.authorName;
  },
  getLabelPickerFunc() {
    return (schedule: JobOverview) => schedule.authorName;
  },
  getFilterFunc(state) {
    return (schedules: JobOverview[]) =>
      filterBy(
        filterBy(
          filterBy(
            filterBy(
              schedules,
              state.selectedFilters.queries,
              singleValueFilterFactory(
                state.selectedFilters.queries,
                j => j.queryName
              )
            ),
            state.selectedFilters.status,
            singleValueFilterFactory(
              state.selectedFilters.status,
              j => j.status
            )
          ),
          state.selectedFilters.repetitionType,
          singleValueFilterFactory(
            state.selectedFilters.repetitionType,
            j => j.repetition?.type?.toLowerCase() || ''
          )
        ),
        state.selectedFilters.stores,
        storeFilterFactory(state.selectedFilters.stores)
      );
  },
};

export const statusFilter: Filter = {
  key: 'status',
  getPickerFunc() {
    return (schedule: JobOverview) => schedule.status;
  },
  getLabelPickerFunc() {
    return (schedule: JobOverview) => schedule.status;
  },
  getFilterFunc(state) {
    return (schedules: JobOverview[]) =>
      filterBy(
        filterBy(
          filterBy(
            filterBy(
              schedules,
              state.selectedFilters.queries,
              singleValueFilterFactory(
                state.selectedFilters.queries,
                j => j.queryName
              )
            ),
            state.selectedFilters.stores,
            storeFilterFactory(state.selectedFilters.stores)
          ),
          state.selectedFilters.repetitionType,
          singleValueFilterFactory(
            state.selectedFilters.repetitionType,
            j => j.repetition?.type?.toLowerCase() || ''
          )
        ),
        state.selectedFilters.authors,
        singleValueFilterFactory(
          state.selectedFilters.authors,
          j => j.authorName
        )
      );
  },
};

export const repetitionTypeFilter: Filter = {
  key: 'repetitionType',
  getPickerFunc() {
    return (schedule: JobOverview) => schedule.repetition.type;
  },
  getLabelPickerFunc() {
    return (schedule: JobOverview) => {
      const type = schedule.repetition?.type || '';
      return type ? type.charAt(0).toUpperCase() + type.slice(1) : '';
    };
  },

  getFilterFunc(state) {
    return (schedules: JobOverview[]) =>
      filterBy(
        filterBy(
          filterBy(
            filterBy(
              filterBy(
                schedules,
                state.selectedFilters.authors,
                singleValueFilterFactory(
                  state.selectedFilters.authors,
                  j => j.authorName
                )
              ),
              state.selectedFilters.status,
              singleValueFilterFactory(
                state.selectedFilters.status,
                j => j.status
              )
            ),
            state.selectedFilters.queries,
            singleValueFilterFactory(
              state.selectedFilters.queries,
              j => j.queryName
            )
          ),
          state.selectedFilters.stores,
          storeFilterFactory(state.selectedFilters.stores)
        ),
        state.selectedFilters.repetitionType,
        singleValueFilterFactory(
          state.selectedFilters.repetitionType,
          j => j.repetition.type
        )
      );
  },
};
